import { useState } from "react";
import { motion } from "framer-motion";
import api, { formatApiErrorDetail } from "@/lib/api";
import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { UserCircle, Lock, EnvelopeOpen, FloppyDisk } from "@phosphor-icons/react";

const SUPPORT_EMAIL = "NewmarketSC2013girls@gmail.com";

export default function Profile() {
  const { user, setUser } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [nameSaving, setNameSaving] = useState(false);
  const [nameError, setNameError] = useState("");

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [pwSaving, setPwSaving] = useState(false);
  const [pwError, setPwError] = useState("");

  const inputCls = "w-full bg-[#0A0A0A] border border-zinc-800 text-white px-3 py-2.5 rounded-sm focus:outline-none focus:border-[#CCFF00] transition-colors duration-150";

  const saveName = async (e) => {
    e.preventDefault();
    setNameError("");
    const trimmed = name.trim();
    if (!trimmed) {
      setNameError("Name can't be empty.");
      return;
    }
    setNameSaving(true);
    try {
      const { data } = await api.put("/auth/me", { name: trimmed });
      setUser(data);
      toast.success("Name updated");
    } catch (err) {
      setNameError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setNameSaving(false);
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    setPwError("");
    if (newPassword.length < 6) {
      setPwError("New password must be at least 6 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setPwError("New password and confirmation don't match.");
      return;
    }
    setPwSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: currentPassword, new_password: newPassword });
      toast.success("Password changed");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      setPwError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setPwSaving(false);
    }
  };

  return (
    <Layout>
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mb-8 max-w-xl">
        <p className="font-label text-xs text-[#CCFF00] mb-1">Your account</p>
        <h1 className="font-heading text-4xl sm:text-5xl text-white leading-none">Profile</h1>
      </motion.div>

      <div className="max-w-xl space-y-8">
        <div className="bg-[#121212] border border-zinc-800 rounded-sm p-6" data-testid="profile-details-card">
          <div className="flex items-center gap-2 mb-5">
            <UserCircle size={20} weight="bold" className="text-[#CCFF00]" />
            <h2 className="font-heading text-xl text-white">Your details</h2>
          </div>
          <form onSubmit={saveName} className="space-y-4">
            <div>
              <label className="font-label text-xs text-zinc-400 block mb-2">Display name</label>
              <input value={name} onChange={(e) => setName(e.target.value)} data-testid="profile-name-input" className={inputCls} />
            </div>
            <div>
              <label className="font-label text-xs text-zinc-400 block mb-2">Email / username</label>
              <input value={user?.email || ""} disabled data-testid="profile-email-input" className={inputCls + " opacity-50 cursor-not-allowed"} />
            </div>
            {nameError && <p className="text-[#FF3B30] text-sm" data-testid="profile-name-error">{nameError}</p>}
            <button type="submit" disabled={nameSaving} data-testid="save-name-btn" className="flex items-center gap-2 bg-[#CCFF00] text-black font-label text-xs px-5 py-2.5 rounded-sm hover:bg-[#B3E600] transition-colors duration-150 disabled:opacity-50">
              <FloppyDisk size={16} weight="bold" /> {nameSaving ? "Saving…" : "Save name"}
            </button>
          </form>
        </div>

        <div className="bg-[#121212] border border-zinc-800 rounded-sm p-6" data-testid="profile-password-card">
          <div className="flex items-center gap-2 mb-5">
            <Lock size={20} weight="bold" className="text-[#CCFF00]" />
            <h2 className="font-heading text-xl text-white">Change password</h2>
          </div>
          <form onSubmit={savePassword} className="space-y-4">
            <div>
              <label className="font-label text-xs text-zinc-400 block mb-2">Current password</label>
              <input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} data-testid="current-password-input" placeholder="••••••••" className={inputCls} />
            </div>
            <div>
              <label className="font-label text-xs text-zinc-400 block mb-2">New password</label>
              <input type="password" minLength={6} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} data-testid="new-password-input" placeholder="At least 6 characters" className={inputCls} />
            </div>
            <div>
              <label className="font-label text-xs text-zinc-400 block mb-2">Confirm new password</label>
              <input type="password" minLength={6} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} data-testid="confirm-password-input" placeholder="Re-enter new password" className={inputCls} />
            </div>
            {pwError && <p className="text-[#FF3B30] text-sm" data-testid="profile-password-error">{pwError}</p>}
            <button type="submit" disabled={pwSaving} data-testid="save-password-btn" className="flex items-center gap-2 bg-[#CCFF00] text-black font-label text-xs px-5 py-2.5 rounded-sm hover:bg-[#B3E600] transition-colors duration-150 disabled:opacity-50">
              <Lock size={16} weight="bold" /> {pwSaving ? "Updating…" : "Update password"}
            </button>
          </form>
        </div>

        <a
          href={`mailto:${SUPPORT_EMAIL}`}
          data-testid="feedback-support-btn"
          className="flex items-center justify-center gap-2 border border-zinc-800 text-zinc-300 hover:text-[#CCFF00] hover:border-[#CCFF00] font-label text-xs px-5 py-3 rounded-sm transition-colors duration-150"
        >
          <EnvelopeOpen size={16} weight="bold" /> Feedback / Support Contact
        </a>
      </div>
    </Layout>
  );
}
